/**
 * Native CTA helper for Find-your-strategy → questionnaire.
 *
 * Belongs here: post CTA, then refetch landing status when the webview resumes.
 * Does not belong here: choosing which CTA object to send (callers pass that).
 */
import { useState } from 'react'
import { useWebViewPageVisibility } from '../../../hooks/useWebViewPageVisibility'
import type { ElitePrimeInsightCta } from '../../../types/elitePrimeSummary.types'
import type { EliteInsightsCta } from '../../../types/misc.types'
import { useElitePlansLanding } from '../ElitePlansLandingContext'
import { postAdviceCardCta } from '../components/advice-cards/postAdviceCardCta'

/**
 * Posts a native CTA. When `opensQuestionnaire` is true, the next webview-resume
 * refetches elite-plans status so Find / In progress / Submitted copy can update.
 *
 * Used by allocation Find-your-strategy preview and advice cards that open the questionnaire.
 */
export function useRefetchStatusAfterStrategyQuestionnaire() {
  const { refetchStatus } = useElitePlansLanding()
  const [awaitingResume, setAwaitingResume] = useState(false)

  useWebViewPageVisibility({
    enabled: awaitingResume,
    onResume: () => {
      setAwaitingResume(false)
      refetchStatus()
    }
  })

  function postStrategyCta(
    cta: EliteInsightsCta | ElitePrimeInsightCta,
    options?: {
      /** True when this CTA opens the strategy questionnaire (not View details / plan). */
      opensQuestionnaire?: boolean
    }
  ) {
    // Questionnaire may change strategy status — refresh after the user returns
    if (options?.opensQuestionnaire === true) {
      setAwaitingResume(true)
    }
    postAdviceCardCta(cta)
  }

  return { postStrategyCta }
}
